import EditorTheme from "../themes/EditorTheme";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";

import ToolbarPlugin from "../plugins/ToolbarPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TRANSFORMERS } from "@lexical/markdown";
import ImagePlugin from "../plugins/ImagePlugin";

import ListMaxIndentLevelPlugin from "../plugins/ListMaxIndentLevelPlugin";
import CodeHighlightPlugin from "../plugins/CodeHighlightPlugin";
import AutoLinkPlugin from "../plugins/AutoLinkPlugin";
import Box from "@mui/material/Box";
import { useTheme, IconButton, Tooltip } from "@mui/material";
import {
  CloseOutlined,
  MinimizeOutlined,
  FullscreenOutlined,
} from "@mui/icons-material";
import { ImageNode } from "../nodes/ImageNode";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import HandleChangePlugin from "../plugins/HandleChangePlugin";
import { useAppDispatch } from "@store/hooks";
import { setPaperEditorMode } from "@store/slices/viewSlice";

import type { RefObject } from "react";
import type Konva from "konva";

function Placeholder() {
  return <div className="editor-placeholder">Start writing...</div>;
}

const editorConfig = {
  theme: EditorTheme,
  namespace: "MyEditor",
  onError(error: any) {
    throw error;
  },
  // Any custom nodes go here
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    CodeHighlightNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    AutoLinkNode,
    LinkNode,
    ImageNode,
  ],
};

interface EditorComponentProps {
  mode?: 'modal' | 'fullscreen';
  stageRef?: RefObject<Konva.Stage>;
}

export default function EditorComponent({ mode = 'modal', stageRef }: EditorComponentProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const handleMinimize = () => {
    dispatch(setPaperEditorMode('modal'));
  };

  const handleMaximize = () => {
    dispatch(setPaperEditorMode('fullscreen'));
  };

  const handleClose = () => {
    dispatch(setPaperEditorMode('hidden'));
  };

  const isFullscreen = mode === 'fullscreen';

  return (
    <Box
      sx={{
        border: `2px ridge ${theme.palette.background.paper}`,
        borderRadius: isFullscreen ? 0 : "7px",
        background: theme.palette.background.default,
        lineHeight: "20px",
        fontWeight: "400",
        textAlign: "left",
        resize: isFullscreen ? "none" : "both",
        overflow: "auto",
        width: isFullscreen ? "100%" : "700px",
        height: isFullscreen ? "100%" : "500px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Title bar with controls */}
      {isFullscreen ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Tooltip title="Minimize to floating window">
            <IconButton onClick={handleMinimize} size="small">
              <MinimizeOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close editor">
            <IconButton onClick={handleClose} size="small">
              <CloseOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box
          id="handle"
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            px: 1,
            py: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
            cursor: 'move',
            bgcolor: 'action.hover',
          }}
        >
          <Tooltip title="Maximize to fullscreen">
            <IconButton
              onClick={handleMaximize}
              size="small"
              id="stop-drag"
            >
              <FullscreenOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close editor">
            <IconButton onClick={handleClose} size="small" id="stop-drag">
              <CloseOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
        }}
      >
        <LexicalComposer initialConfig={editorConfig}>
          <ToolbarPlugin stageRef={stageRef} />
          <Box
            sx={{
              position: "relative",
              cursor: "text",
            }}
            id="stop-drag"
          >
            <RichTextPlugin
              contentEditable={<ContentEditable className="editor-input" />}
              placeholder={<Placeholder />}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <ImagePlugin />
            <TabIndentationPlugin />
            <HistoryPlugin />
            <AutoFocusPlugin />
            <CodeHighlightPlugin />
            <ListPlugin />
            <LinkPlugin />
            <AutoLinkPlugin />
            <HandleChangePlugin />
            <ListMaxIndentLevelPlugin maxDepth={7} />
            <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
            <ClearEditorPlugin />
          </Box>
        </LexicalComposer>
      </Box>
    </Box>
  );
}
